import { useState } from "react";
import { Paperclip, Trash2, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { dimsFromVariant, fieldLabel, fieldsFor, newLine, QTY_UNITS } from "@/lib/calc";
import { uploadFile, errMsg } from "@/lib/api";
import { toast } from "sonner";

function ProductPicker({ products, value, onPick, idx }) {
  const [open, setOpen] = useState(false);
  const cur = products.find((p) => p.slug === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`line-${idx}-product`} className="field text-left truncate">{cur ? cur.name : "Select a product…"}</button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px] bg-[var(--paper)]" align="start">
        <Command>
          <CommandInput placeholder="Search products…" data-testid={`line-${idx}-product-search`} />
          <CommandList>
            <CommandEmpty>No product found.</CommandEmpty>
            {products.map((p) => (
              <CommandItem key={p.slug} value={p.name} data-testid={`line-${idx}-option-${p.slug}`} onSelect={() => { onPick(p); setOpen(false); }}>{p.name}</CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function LineItemEditor({ idx, line, products, onChange, onRemove, withAttachment, errors = {}, children }) {
  const p = products.find((x) => x.slug === line.product_slug);
  const [busy, setBusy] = useState(false);
  const set = (patch) => onChange({ ...line, ...patch });
  const pick = (np) => onChange({ ...newLine(np, { unit: line.unit, quantity: line.quantity, qty_unit: line.qty_unit }), key: line.key });
  const setVariant = (name) => set({ variant: name, dims: dimsFromVariant(p.variants.find((v) => v.name === name), line.unit) });
  const setUnit = (u) => {
    if (u === line.unit) return;
    const f = u === "inch" ? 1 / 25.4 : 25.4;
    const dims = Object.fromEntries(Object.entries(line.dims || {}).map(([k, v]) => [k, v === "" || v == null ? v : +(v * f).toFixed(u === "inch" ? 3 : 1)]));
    set({ unit: u, dims });
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    setBusy(true);
    try { set({ attachment: await uploadFile(f) }); toast.success("Drawing attached"); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const fields = fieldsFor(p);
  const isStd = p?.dimension_profile === "STANDARD";

  return (
    <div data-testid={`line-item-${idx}`} className="card p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <p className="eyebrow">Line {String(idx + 1).padStart(2, "0")}</p>
        {onRemove && <button type="button" data-testid={`line-${idx}-remove`} onClick={onRemove} className="text-[var(--steel)] hover:text-[#A33A2B] text-xs inline-flex items-center gap-1 transition-colors"><Trash2 size={14} /> Remove</button>}
      </div>
      <div className="grid md:grid-cols-6 gap-4">
        <label className="md:col-span-6"><span className="label">Product *</span><ProductPicker idx={idx} products={products} value={line.product_slug} onPick={pick} />{errors.product && <p className="err">{errors.product}</p>}</label>
        <label className="md:col-span-3"><span className="label">{isStd ? "Size" : "Variant / sub-type"}</span>
          <select data-testid={`line-${idx}-variant`} className="field" value={line.variant} disabled={!p} onChange={(e) => setVariant(e.target.value)}>
            {(p?.variants || []).map((v) => <option key={v.name}>{v.name}</option>)}
            {!isStd && p && <option value="Custom">Custom (to drawing)</option>}
          </select>
        </label>
        <label className="md:col-span-3"><span className="label">Finish</span>
          <select data-testid={`line-${idx}-finish`} className="field" value={line.finish} disabled={!p} onChange={(e) => set({ finish: e.target.value })}>
            {(p?.finishes || []).map((f) => <option key={f}>{f}</option>)}
          </select>
        </label>
        {fields.length > 0 && (
          <>
            <div className="md:col-span-6 flex items-center gap-3 -mb-1">
              <span className="label !mb-0">Dimensions</span>
              <div className="inline-flex border border-[var(--stone)] text-xs">{["mm", "inch"].map((u) => <button type="button" key={u} data-testid={`line-${idx}-unit-${u}`} onClick={() => setUnit(u)} className={`px-3 py-1 transition-colors ${line.unit === u ? "bg-[var(--ink)] text-[var(--paper)]" : ""}`}>{u}</button>)}</div>
            </div>
            {fields.map((k) => (
              <label key={k} className="md:col-span-1"><span className="label normal-case tracking-normal">{fieldLabel(p, k)}</span>
                <input data-testid={`line-${idx}-dim-${k}`} type="number" min="0" step="any" className="field tabular" value={line.dims?.[k] ?? ""} onChange={(e) => set({ dims: { ...line.dims, [k]: e.target.value } })} />
              </label>
            ))}
            {p?.dimension_profile === "ROD" && (
              <label className="md:col-span-2"><span className="label">Thread spec</span><input data-testid={`line-${idx}-thread`} className="field" placeholder="e.g. 1/2-13 UNC" value={line.thread_spec} onChange={(e) => set({ thread_spec: e.target.value })} /></label>
            )}
          </>
        )}
        <label className="md:col-span-2"><span className="label">Required quantity *</span>
          <input data-testid={`line-${idx}-qty`} type="number" min="0" className="field tabular" aria-invalid={!!errors.quantity} value={line.quantity} onChange={(e) => set({ quantity: e.target.value })} />
          {errors.quantity && <p className="err">{errors.quantity}</p>}
        </label>
        <label className="md:col-span-1"><span className="label">Unit</span>
          <select data-testid={`line-${idx}-qty-unit`} className="field" value={line.qty_unit} onChange={(e) => set({ qty_unit: e.target.value })}>{QTY_UNITS.map((u) => <option key={u}>{u}</option>)}</select>
        </label>
        {withAttachment && (
          <div className="md:col-span-3 flex items-end">
            <label className="btn btn-outline btn-sm cursor-pointer">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} {line.attachment ? "Replace drawing" : "Attach drawing / spec"}
              <input data-testid={`line-${idx}-file`} type="file" accept=".pdf,.jpg,.jpeg,.png,.dwg" className="hidden" onChange={onFile} />
            </label>
            {line.attachment && <span className="ml-3 text-xs text-[var(--graphite)] truncate">{line.attachment.name}</span>}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
